import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import agribegriLogo from '../images/agribegri_logo.png';
import NotificationBell from './NotificationBell';

// Icons (using simple inline SVGs as placeholders)
const DashboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
);

const PostsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
);

const CommentsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
);

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
);

const AllCommentsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><line x1="8" y1="9" x2="16" y2="9"></line><line x1="8" y1="13" x2="14" y2="13"></line></svg>
);

const sections = [
  { label: 'Dashboard', key: 'dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Posts', key: 'posts', path: '/posts', icon: <PostsIcon /> },
  { label: 'Comments', key: 'comments', path: '/comments', icon: <CommentsIcon /> },
  { label: 'All Comments', key: 'allcomments', path: '/admin-comments', icon: <AllCommentsIcon /> },
  // { label: 'Users', key: 'users', path: '/users', icon: <UsersIcon /> },
];

const Sidebar = ({ active }) => {
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const userName = localStorage.getItem('userName') || 'User';

  // Handle Escape key globally
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showLogoutDialog && !isLoggingOut) {
        setShowLogoutDialog(false);
      }
    };

    if (showLogoutDialog) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showLogoutDialog, isLoggingOut]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    // Simulate a small delay for better UX (you can remove this in production)
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      localStorage.clear();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <aside style={{
        width: 280,
        background: '#82C853',
        color: '#1F1010',
        padding: '1.5rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        boxSizing: 'border-box',
        transition: 'all 0.3s ease',
        fontFamily: 'Poppins, sans-serif'
      }}>
        {/* Logo Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '0 12px',
          marginBottom: '2.5rem',
        }}>
          <img
            src={agribegriLogo}
            alt="Agribegri Logo"
            style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: '#fff' }}
          />
          <span style={{ 
            fontWeight: 600, 
            fontSize: '1.25rem', 
            color: '#1F1010',
            letterSpacing: '0.5px',
            fontFamily: 'Poppins, sans-serif'
          }}>Darshaka</span>
          <NotificationBell />
        </div>

        {/* Navigation Section */}
        <nav style={{ flexGrow: 1 }}>
          {sections.map(section => (
            <button
              key={section.label}
              onClick={() => navigate(section.path)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                background: active === (section.key ?? section.label.toLowerCase()) ? '#f0f9e8' : 'transparent',
                color: '#1F1010',
                fill: '#1F1010',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '10px',
                marginBottom: '8px',
                fontWeight: 500,
                fontSize: '0.85rem',
                boxShadow: active === (section.key ?? section.label.toLowerCase()) ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.2s ease',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif',
                ':hover': {
                  background: active === section.label.toLowerCase() ? '#f0f9e8' : '#f5f5f5',
                  transform: 'translateX(4px)',
                }
              }}
            >
              {section.icon}
              {section.label}
            </button>
          ))}
        </nav>

        {/* User Profile Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '9 12px',
          borderTop: '1px solid #eee',
          marginTop: 'auto',
          background: '#fafafa',
          borderRadius: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: '#f0f9e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1F1010',
              fontWeight: 600,
              fontSize: '1.1rem',
            }}>
              {userName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ 
                fontWeight: 600, 
                fontSize: '0.95rem', 
                color: '#1F1010',
                marginBottom: '2px',
                fontFamily: 'Poppins, sans-serif'
              }}>{userName}</div>
            </div>
          </div>
          <button
            onClick={() => setShowLogoutDialog(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1F1010',
              fill: '#1F1010',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '10px',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.1)';
              e.target.style.transform = 'scale(1.1)';
              e.target.style.color = '#ef4444';
              e.target.style.fill = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.transform = 'scale(1)';
              e.target.style.color = '#1F1010';
              e.target.style.fill = '#1F1010';
            }}
            title="Logout (Click to confirm)"
          >
            <LogoutIcon />
          </button>
        </div>
      </aside>

      {/* Enhanced Logout Dialog */}
      {showLogoutDialog && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
            animation: 'fadeIn 0.2s ease-out',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isLoggingOut) {
              setShowLogoutDialog(false);
            }
          }}
        >
          <div 
            style={{
              background: 'white',
              padding: '32px',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25), 0 8px 32px rgba(0, 0, 0, 0.15)',
              width: '400px',
              maxWidth: '90%',
              animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Decorative Background */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #82C853, #1b5e20)',
              borderRadius: '20px 20px 0 0',
            }} />
            
            {/* Icon */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px',
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(238, 90, 82, 0.3)',
                animation: 'pulse 2s infinite',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '1.4rem', 
              color: '#1a1a1a',
              fontWeight: 700,
              fontFamily: 'Poppins, sans-serif',
              textAlign: 'center',
              letterSpacing: '-0.5px',
            }}>
              Confirm Logout
            </h3>

            {/* Message */}
            <p style={{ 
              margin: '0 0 32px 0', 
              color: '#666',
              fontSize: '1rem',
              lineHeight: '1.6',
              fontFamily: 'Poppins, sans-serif',
              textAlign: 'center',
              maxWidth: '320px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              Are you sure you want to logout? You will need to login again to access your account.
            </p>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
            }}>
              <button
                onClick={() => setShowLogoutDialog(false)}
                disabled={isLoggingOut}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && !isLoggingOut) {
                    setShowLogoutDialog(false);
                  }
                }}
                style={{
                  padding: '14px 28px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '12px',
                  background: 'white',
                  cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  color: isLoggingOut ? '#9ca3af' : '#64748b',
                  fontWeight: 600,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: 'Poppins, sans-serif',
                  minWidth: '120px',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: isLoggingOut ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isLoggingOut) {
                    e.target.style.background = '#f8fafc';
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoggingOut) {
                    e.target.style.background = 'white';
                    e.target.style.borderColor = '#e1e5e9';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoggingOut) {
                    handleLogout();
                  }
                }}
                style={{
                  padding: '14px 28px',
                  border: 'none',
                  borderRadius: '12px',
                  background: isLoggingOut 
                    ? 'linear-gradient(135deg, #9ca3af, #6b7280)' 
                    : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: 'white',
                  cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 600,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: 'Poppins, sans-serif',
                  minWidth: '120px',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: isLoggingOut 
                    ? '0 4px 16px rgba(156, 163, 175, 0.3)' 
                    : '0 4px 16px rgba(239, 68, 68, 0.3)',
                  opacity: isLoggingOut ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isLoggingOut) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(239, 68, 68, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoggingOut) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.3)';
                  }
                }}
              >
                {isLoggingOut ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px',
                      display: 'inline-block',
                    }}></div>
                    Logging out...
                  </>
                ) : (
                  'Logout'
                )}
              </button>
            </div>

            {/* Keyboard Shortcut Hint */}
            <div style={{
              textAlign: 'center',
              marginTop: '20px',
              fontSize: '0.8rem',
              color: '#94a3b8',
              fontFamily: 'Poppins, sans-serif',
            }}>
              Press <kbd style={{
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
              }}>Esc</kbd> to cancel
            </div>
          </div>
        </div>
      )}

      {/* Enhanced CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 8px 24px rgba(238, 90, 82, 0.3);
          }
          50% { 
            transform: scale(1.05);
            box-shadow: 0 12px 32px rgba(238, 90, 82, 0.4);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default Sidebar; 